import {
  Modal,
  TextInput,
  Button,
  Stack,
  Group,
  Select,
  Textarea,
  Switch,
  NumberInput,
  Collapse,
  MultiSelect,
  Paper,
  Text,
} from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { useEffect } from 'react';
import { customFieldSchema, type CustomFieldFormValues } from '../schemas/customFieldSchema';
import type { CustomFieldDefinition } from '@/hooks/api/useCustomFields';
import { useCategories } from '@/hooks/api/useCategories';
import { useOrganization } from '@/hooks/useOrganization';

interface CustomFieldFormProps {
  opened: boolean;
  onClose: () => void;
  onSubmit: (values: CustomFieldFormValues, keepOpen?: boolean) => void;
  field?: CustomFieldDefinition | null;
  isLoading?: boolean;
  nextOrder: number;
}

export function CustomFieldForm({
  opened,
  onClose,
  onSubmit,
  field,
  isLoading,
  nextOrder,
}: CustomFieldFormProps) {
  const { organizationId } = useOrganization();
  const { data: categories = [] } = useCategories(organizationId);

  const categoryOptions = categories.map((cat) => ({
    value: cat.id,
    label: cat.name,
  }));

  const form = useForm<CustomFieldFormValues>({
    validate: zodResolver(customFieldSchema),
    initialValues: {
      name: '',
      field_type: 'text',
      options: '',
      is_required: false,
      default_value: '',
      display_order: nextOrder,
      is_active: true,
      limit_to_categories: false,
      category_ids: [],
    },
  });

  useEffect(() => {
    if (field) {
      const hasCategories = (field.category_ids || []).length > 0;
      form.setValues({
        name: field.name,
        field_type: field.field_type,
        options: Array.isArray(field.options) ? field.options.join('\n') : '',
        is_required: field.is_required,
        default_value: field.default_value || '',
        display_order: field.display_order,
        is_active: field.is_active,
        limit_to_categories: hasCategories,
        category_ids: field.category_ids || [],
      });
    } else {
      form.reset();
      form.setFieldValue('display_order', nextOrder);
    }
  }, [field, opened, nextOrder]);

  const handleSubmit = (values: CustomFieldFormValues, keepOpen = false) => {
    onSubmit(values, keepOpen);
    if (keepOpen) {
      form.reset();
      form.setFieldValue('display_order', nextOrder + 1);
    }
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const showOptions = form.values.field_type === 'dropdown';

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={field ? 'Edit Custom Field' : 'Create Custom Field'}
      size="md"
    >
      <form
        onSubmit={form.onSubmit(handleSubmit)}
        onKeyDown={(e) => {
          // Allow Enter to submit form from any field (except textareas)
          if (e.key === 'Enter' && (e.target as HTMLElement).tagName !== 'TEXTAREA') {
            e.preventDefault();
            form.onSubmit(handleSubmit)();
          }
        }}
      >
        <Stack gap="md">
          <TextInput
            label="Field Name"
            placeholder="e.g., Warranty Expiration, Purchase Order Number"
            required
            {...form.getInputProps('name')}
          />

          <Select
            label="Field Type"
            placeholder="Select field type"
            required
            data={[
              { value: 'text', label: 'Text' },
              { value: 'number', label: 'Number' },
              { value: 'date', label: 'Date' },
              { value: 'dropdown', label: 'Dropdown' },
              { value: 'textarea', label: 'Text Area' },
              { value: 'checkbox', label: 'Checkbox' },
            ]}
            {...form.getInputProps('field_type')}
          />

          {showOptions && (
            <Textarea
              label="Dropdown Options"
              placeholder="Enter one option per line"
              description="Each line will be a separate option"
              required
              minRows={4}
              {...form.getInputProps('options')}
            />
          )}

          <TextInput
            label="Default Value"
            placeholder="Optional default value"
            {...form.getInputProps('default_value')}
          />

          <NumberInput
            label="Display Order"
            description="Lower numbers appear first"
            min={0}
            {...form.getInputProps('display_order')}
          />

          <Switch
            label="Required Field"
            description="Users must provide a value for this field"
            {...form.getInputProps('is_required', { type: 'checkbox' })}
          />

          <Switch
            label="Active"
            description="Inactive fields won't appear in forms"
            {...form.getInputProps('is_active', { type: 'checkbox' })}
          />

          <Switch
            label="Limit to specific categories"
            description="By default, this field appears for all assets"
            {...form.getInputProps('limit_to_categories', { type: 'checkbox' })}
          />

          <Collapse in={form.values.limit_to_categories}>
            <Paper p="sm" withBorder bg="gray.0" mt="xs">
              <Stack gap="sm">
                <Text size="sm" c="dimmed">
                  Select which categories this field should appear for:
                </Text>
                <MultiSelect
                  data={categoryOptions}
                  placeholder="Select categories"
                  searchable
                  clearable
                  {...form.getInputProps('category_ids')}
                />
                {form.values.category_ids && form.values.category_ids.length > 0 && (
                  <Text size="xs" c="dimmed">
                    This field will only appear on assets in the selected {form.values.category_ids.length} category(ies)
                  </Text>
                )}
              </Stack>
            </Paper>
          </Collapse>

          <Group justify="space-between" mt="md" wrap="nowrap">
            <Button variant="subtle" onClick={handleClose} disabled={isLoading}>
              Cancel
            </Button>
            <Group gap="sm" wrap="nowrap">
              {!field && (
                <Button
                  type="button"
                  variant="light"
                  onClick={() => form.onSubmit((values) => handleSubmit(values, true))()}
                  loading={isLoading}
                >
                  Create & Add Another
                </Button>
              )}
              <Button
                type="button"
                onClick={() => form.onSubmit((values) => handleSubmit(values, false))()}
                loading={isLoading}
              >
                {field ? 'Update Field' : 'Create Field'}
              </Button>
            </Group>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
